import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { 
  useLocation, 
  useNavigationType, 
  createRoutesFromChildren, 
  matchRoutes 
} from 'react-router-dom';

/**
 * Initialize Sentry for client-side error tracking
 * Call this in main.tsx before rendering the app
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_ENVIRONMENT || 'development';

  // Only initialize Sentry if DSN is configured
  if (!dsn) {
    // Silently skip initialization - no warning needed in development
    return;
  }

  try {
    Sentry.init({
      dsn,
      environment,
      
      // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring
      // In production, reduce this to a lower percentage (e.g., 0.1 for 10%)
      tracesSampleRate: environment === 'production' ? 0.1 : 1.0,

      // Set sample rate for profiling
      // This is relative to tracesSampleRate
      profilesSampleRate: environment === 'production' ? 0.1 : 1.0,

      // Capture Replay for 10% of all sessions,
      // plus 100% of sessions with an error
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,

      integrations: [
        // Browser profiling
        Sentry.browserTracingIntegration(),
        
        // Session replay
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),

        // React error boundary with React Router
        Sentry.reactRouterV6BrowserTracingIntegration({
          useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
      ],

      // Filter out low-value errors
      ignoreErrors: [
        // Network errors
        'Network request failed',
        'Failed to fetch',
        'NetworkError',
        'Load failed',
        
        // Browser extensions
        'top.GLOBALS',
        'chrome-extension://',
        'moz-extension://',
        
        // Random plugins/extensions
        'Can\'t find variable: ZiteReader',
        'jigsaw is not defined',
        'ComboSearch is not defined',
        
        // Facebook/Social media
        'fb_xd_fragment',

        // Ignore ResizeObserver errors (non-critical)
        'ResizeObserver loop limit exceeded',
      ],

      // Scrub sensitive data
      beforeSend(event) {
        // Remove sensitive headers
        if (event.request?.headers) {
          delete event.request.headers['Authorization'];
          delete event.request.headers['Cookie'];
        }

        // Remove email addresses from error messages
        if (event.message) {
          event.message = event.message.replace(/[\w.-]+@[\w.-]+\.\w+/g, '[EMAIL]');
        }

        // Remove API keys from URLs
        if (event.request?.url) {
          event.request.url = event.request.url.replace(/apikey=[^&]+/g, 'apikey=[REDACTED]');
        }

        return event;
      },
    });
  } catch (error) {
    // Silently fail if Sentry is blocked by ad blocker
    console.debug('Sentry initialization skipped (likely blocked by browser extension)');
  }
}

/**
 * Set user context for Sentry
 * Call this after successful authentication
 */
export function setSentryUser(user: { id: string; email?: string; username?: string }) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.username,
  });
}

/**
 * Clear user context (on logout)
 */
export function clearSentryUser() {
  Sentry.setUser(null);
}

/**
 * Manually capture an exception
 */
export function captureException(error: Error, context?: Record<string, any>) {
  Sentry.captureException(error, {
    extra: context,
  });
}

/**
 * Manually capture a message
 */
export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info') {
  Sentry.captureMessage(message, level);
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(message: string, category: string, data?: Record<string, any>) {
  Sentry.addBreadcrumb({
    message,
    category,
    level: 'info',
    data,
  });
}

/**
 * Set custom tags
 */
export function setTag(key: string, value: string) {
  Sentry.setTag(key, value);
}

/**
 * Set custom context
 */
export function setContext(name: string, context: Record<string, any>) {
  Sentry.setContext(name, context);
}

/**
 * Start a transaction for performance monitoring
 * @deprecated Use Sentry.startSpan() instead in newer versions
 */
export function startTransaction(name: string, op: string) {
  return Sentry.startSpan({
    name,
    op,
  }, (span) => span);
}

// Re-export Sentry for advanced usage
export { Sentry };
